import { watch } from 'vue'
import { useCookie } from '#app'
/**
 * Dark-mode handling relies on two cookies:
 *  - `theme` stores the user preference (`light` | `dark` | `system`).
 *  - `theme_resolved` stores the last resolved state (`light` | `dark`).
 *    This allows the server render to immediately know which class to apply,
 *    avoiding a flash of the wrong theme before hydration.
 */

type ThemePreference = 'light' | 'dark' | 'system'
const THEME_COOKIE = 'theme'
const RESOLVED_THEME_COOKIE = 'theme_resolved'
let hasSetup = false
let systemDarkQuery: MediaQueryList | null = null

export const useDarkMode = () => {
  const themeCookie = useCookie<ThemePreference>(THEME_COOKIE, {
    sameSite: 'lax',
    path: '/',
  })

  const resolvedThemeCookie = useCookie<'light' | 'dark'>(
    RESOLVED_THEME_COOKIE,
    {
      sameSite: 'lax',
      path: '/',
    },
  )

  const theme = useState<ThemePreference>(
    'theme',
    () => themeCookie.value ?? 'system',
  )
  const initialResolved =
    resolvedThemeCookie.value ?? (theme.value === 'dark' ? 'dark' : undefined)
  const isDarkMode = useState<boolean>(
    'isDarkMode',
    () => initialResolved === 'dark',
  )
  const systemThemeHandler = () => {
    if (theme.value === 'system') {
      updateDarkMode()
    }
  }

  const resolveSystemPreference = () => {
    if (typeof window === 'undefined') {
      return false
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  const applyDarkModeClass = (value: boolean) => {
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark-mode', value)
    }
  }

  const updateDarkMode = () => {
    const resolvedDark =
      theme.value === 'system'
        ? (systemDarkQuery?.matches ?? resolveSystemPreference())
        : theme.value === 'dark'

    isDarkMode.value = resolvedDark

    if (typeof window !== 'undefined') {
      applyDarkModeClass(resolvedDark)
      localStorage.setItem('theme', theme.value)
    }

    themeCookie.value = theme.value
    resolvedThemeCookie.value = resolvedDark ? 'dark' : 'light'
  }

  useHead(() => ({
    htmlAttrs: {
      class: isDarkMode.value ? 'dark-mode' : 'light-mode',
    },
  }))

  if (import.meta.client && !hasSetup) {
    hasSetup = true
    systemDarkQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const storedTheme = localStorage.getItem('theme') as ThemePreference | null
    if (storedTheme) {
      theme.value = storedTheme
    } else {
      localStorage.setItem('theme', theme.value)
    }

    updateDarkMode()
    systemDarkQuery.addEventListener('change', systemThemeHandler)

    watch(theme, () => {
      updateDarkMode()
    })
  }

  return {
    theme,
    isDarkMode,
  }
}
